export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"] as const
export type AllowedImageType = (typeof ALLOWED_IMAGE_TYPES)[number]

export const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
export const MAX_AVATAR_SIZE = 512 // Max dimension for avatars
export const AVATAR_QUALITY = 0.85 // JPEG quality for avatars

export interface ImageValidationResult {
	valid: boolean
	error?: string
}

function isAllowedImageType(type: string): type is AllowedImageType {
	return ALLOWED_IMAGE_TYPES.includes(type as AllowedImageType)
}

export function validateImageFile(file: File): ImageValidationResult {
	// Check file type using type guard
	if (!isAllowedImageType(file.type)) {
		return {
			valid: false,
			error: `Unsupported file type. Allowed: ${ALLOWED_IMAGE_TYPES.map(t => t.replace("image/", "")).join(", ")}`,
		}
	}

	// Check file size
	if (file.size > MAX_FILE_SIZE) {
		return {
			valid: false,
			error: `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`,
		}
	}

	return { valid: true }
}

/** Resize and compress image for avatar use (max 512px, JPEG output) */
export async function optimizeAvatarImage(file: File): Promise<File> {
	return new Promise((resolve, reject) => {
		const img = new Image()
		const canvas = document.createElement("canvas")
		const ctx = canvas.getContext("2d")

		if (!ctx) {
			reject(new Error("Canvas not supported"))
			return
		}

		const objectUrl = URL.createObjectURL(file)

		img.onload = () => {
			// Clean up object URL to prevent memory leak
			URL.revokeObjectURL(objectUrl)

			// Calculate new dimensions (square crop, max 512px)
			const size = Math.min(img.width, img.height, MAX_AVATAR_SIZE)

			// Set canvas size
			canvas.width = size
			canvas.height = size

			// Calculate crop position (center crop)
			const sx = (img.width - size) / 2
			const sy = (img.height - size) / 2

			// Draw cropped and resized image
			ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size)

			// Convert to blob
			canvas.toBlob(
				blob => {
					if (!blob) {
						reject(new Error("Failed to optimize image"))
						return
					}

					// Create new file with same name but optimized content
					const optimizedFile = new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), {
						type: "image/jpeg",
					})

					resolve(optimizedFile)
				},
				"image/jpeg",
				AVATAR_QUALITY,
			)
		}

		img.onerror = () => {
			// Clean up object URL on error too
			URL.revokeObjectURL(objectUrl)
			reject(new Error("Failed to load image"))
		}

		// Load image from file
		img.src = objectUrl
	})
}

export function readFileAsBase64(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => {
			const result = reader.result
			if (typeof result !== "string") {
				reject(new Error("Failed to read file as string"))
				return
			}
			// Remove data URL prefix
			const base64 = result.split(",")[1]
			if (!base64) {
				reject(new Error("Failed to read file"))
				return
			}
			resolve(base64)
		}
		reader.onerror = () => reject(new Error("Failed to read file"))
		reader.readAsDataURL(file)
	})
}
